import { Box, Button, IconButton, Modal, TextField, Typography } from "@mui/material"
import { useState } from "react";
import modalStyle from "../../styles/modalStyle";
import CloseIcon from '@mui/icons-material/Close';
import apiClient from "../../axiosClient";
import DropDown from "../baseComponents/dropdown";
import { RootState } from "../../store/store";
import { useSelector } from "react-redux";
import { validateUrl } from "../../utils/commonUtils";

interface ModalProps {
    openModal: boolean;
    handleCloseModal: () => void;
    fetchContracts: () => Promise<void>;
}

interface ClientAgencyContractRequest {
    agency_name: string;
    client_name: string;
    contract_link: string;
}

const AddClientAgencyContractModal: React.FC<ModalProps> = ({ openModal, handleCloseModal, fetchContracts }) => {
    const [contract, setContract] = useState<ClientAgencyContractRequest>({
        agency_name: '',
        client_name: '',
        contract_link: '',
    });

    const agencies = useSelector((state: RootState) => state.agencies.agencies);
    const clients = useSelector((state: RootState) => state.clients.clients);

    const handleSetContract = <K extends keyof ClientAgencyContractRequest>(
        key: K,
        value: ClientAgencyContractRequest[K]
    ) => {
        setContract((prev) => ({ ...prev, [key]: value }));
    };

    const handleSubmit = async () => {
        // Basic validation
        if (
            !contract.agency_name ||
            !contract.client_name ||
            !contract.contract_link
        ) {
            alert('Please fill all required fields');
            return;
        }

        const result = validateUrl(contract.contract_link);
        if (result !== true) {
          alert('Invalid contract link: ' + result);
          return;
        }

        try {
            const response = await apiClient.post('/api/v1/contracts', contract);
            if (response.status === 200) {
                alert('Contract created successfully');
                await fetchContracts();
                closeModal();
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error: ' + error.response.data.message);
            } else if (error.request) {
                alert('No response received: ' + error.request);
            } else {
                alert('Error: ' + error.message);
            }
        }
    };

    const closeModal = () => {
        setContract({
            agency_name: '',
            client_name: '',
            contract_link: '',
        });
        handleCloseModal();
    };

    return (
        <Modal open={openModal} onClose={closeModal}>
            <Box sx={modalStyle}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="h6">
                        Create New Contract
                    </Typography>
                    <IconButton onClick={closeModal}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box mt={2}>
                    {/* Agency Dropdown */}
                    <DropDown
                        label="Select Agency"
                        items={agencies}
                        keyProp="agency_name"
                        valueProp="agency_name"
                        displayProp="name"
                        selectedValue={contract.agency_name}
                        onSelection={(val: string) => handleSetContract('agency_name', val)}
                    />
                </Box>

                <Box mt={2}>
                    {/* Client Dropdown */}
                    <DropDown
                        label="Select Client"
                        items={clients}
                        keyProp="client_name"
                        valueProp="client_name"
                        displayProp="name"
                        selectedValue={contract.client_name}
                        onSelection={(val: string) => handleSetContract('client_name', val)}
                    />
                </Box>

                <Box mt={2}>
                    {/* Contract Link TextField */}
                    <TextField
                        label="Contract Link (URL)"
                        value={contract.contract_link}
                        onChange={(e) => handleSetContract('contract_link', e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                </Box>

                <Box mt={4} textAlign="right">
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default AddClientAgencyContractModal;
